import React, { useMemo, useState } from "react";
import { TextField, Button, Box, Typography, Chip } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import GenericInputField from "../../GenericInputField";
import { colorGrayDark, colorGrayLight } from "../../../styles/theme";

interface NewsletterFormProps {
	onSubmit: (
		email_subject: string,
		newsletter_introduction: string,
		recipients_emails: string[]
	) => void;
}

const sharedButtonStyles = {
	borderRadius: "20px !important",
	cursor: "pointer",
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	height: "50px",
	width: "300px",
	textTransform: "none",
	alignSelf: "end",
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NewsletterForm: React.FC<NewsletterFormProps> = ({ onSubmit }) => {
	const [emailSubject, setEmailSubject] = useState<string>("");
	const [introduction, setIntroduction] = useState<string>("");
	const [recipientsEmails, setRecipientsEmails] = useState<string[]>([]);

	const invalidRecipientEmails = useMemo(
		() => recipientsEmails.filter((e) => !emailRegex.test(e)),
		[recipientsEmails]
	);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		onSubmit(emailSubject, introduction, recipientsEmails);
		setEmailSubject("");
		setIntroduction("");
		setRecipientsEmails([]);
	};

	return (
		<Box
			component="form"
			onSubmit={handleSubmit}
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 2,
				width: 500,
				justifyContent: "space-between",
				height: "100%",
			}}
		>
			<Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
				<NewsletterField
					title={"Email Subject"}
					value={emailSubject}
					onChange={(value) => setEmailSubject(value)}
				/>
				<NewsletterField
					title={"Newsletter Introduction"}
					value={introduction}
					onChange={(value) => setIntroduction(value)}
				/>
				<Box>
					<Typography sx={{ mb: "5px" }}>Recipient Emails</Typography>
					<Autocomplete
						sx={{
							backgroundColor: "transparent",
							border: `1px solid ${colorGrayDark}`,
							borderRadius: "20px",
							"& .MuiInputBase-root.MuiAutocomplete-inputRoot": {
								flexWrap: "wrap",
								maxHeight: 120,
								overflowY: "auto",
							},
						}}
						multiple
						freeSolo
						options={[] as string[]}
						value={recipientsEmails}
						onChange={(event, newValue) =>
							setRecipientsEmails(newValue as string[])
						}
						renderTags={(value, getTagProps) =>
							value.map((option, index) => {
								const isValid = !invalidRecipientEmails.includes(option);
								// const isValid = emailRegex.test(option);
								return (
									<Chip
										key={option}
										label={option}
										{...getTagProps({ index })}
										color={isValid ? "default" : "error"}
										sx={
											!isValid
												? {
														backgroundColor: (theme) =>
															theme.palette.error.main,
														color: "#fff",
												  }
												: undefined
										}
									/>
								);
							})
						}
						renderInput={(params) => (
							<TextField
								maxRows={3}
								{...params}
								sx={{
									"& .MuiOutlinedInput-root": {
										"& fieldset": {
											border: "none",
										},
										"&:hover fieldset": {
											border: "none",
										},
										"&.Mui-focused fieldset": {
											border: "none",
										},
									},
									"& .MuiOutlinedInput-root.Mui-focused": {
										outline: "none",
									},
								}}
							/>
						)}
					/>
				</Box>
			</Box>

			<Button
				variant="outlined"
				sx={{ ...sharedButtonStyles }}
				type="submit"
				disabled={
					!emailSubject ||
					!introduction ||
					!recipientsEmails.length ||
					invalidRecipientEmails.length > 0
				}
			>
				Send Newsletter
			</Button>
		</Box>
	);
};

const NewsletterField: React.FC<{
	title: string;
	value: string;
	onChange: (value: string) => void;
}> = ({ title, value, onChange }) => {
	return (
		<Box>
			<Typography sx={{ mb: "5px" }}>{title}</Typography>
			<TextField
				value={value}
				placeholder={""}
				sx={{
					backgroundColor: "transparent",
					border: `1px solid ${colorGrayDark}`,
					borderRadius: "20px",
				}}
				fullWidth
				onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
					onChange(e.target.value)
				}
				hiddenLabel
				InputProps={{
					classes: {
						root: "input-root",
						focused: "input-focused",
					},
				}}
				multiline
				rows={3}
			/>
			{/* <GenericInputField
					multiline
					currentValue={value}
					placeholder={""}
					onValueChange={(e) => onChange(e.target.value)}
				/> */}
		</Box>
	);
};

export default NewsletterForm;
